import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, RefreshCw, CreditCard } from 'lucide-react';
import { creditsCache } from '@/utils/creditsCache';
import { creditsEvents } from '@/utils/creditsEvents';
import { useToast } from '@/hooks/use-toast';

interface PayPalSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseDetails: {
    credits: number;
    amount: number;
    packageName: string;
  };
  userId: string;
}

export function PayPalSuccessModal({ isOpen, onClose, purchaseDetails, userId }: PayPalSuccessModalProps) {
  const [refreshing, setRefreshing] = useState(false);
  const { toast } = useToast();

  const handleRefreshCredits = async () => {
    try {
      setRefreshing(true);
      
      // Clear the credits cache to force fresh data
      creditsCache.clearUser(userId);
      
      // Emit credits event to trigger refresh in CreditBalanceCard
      creditsEvents.emit({ userId, action: 'adjust' });
      
      toast({
        title: "Credits Refreshed",
        description: "Your credit balance has been updated successfully",
      });
      
      // Close modal after successful refresh
      onClose();
    } catch (error) {
      console.error('Error refreshing credits:', error);
      toast({
        title: "Refresh Failed",
        description: "Failed to refresh credits. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            Purchase Completed Successfully!
          </DialogTitle>
          <DialogDescription>
            Your PayPal payment has been processed and your credits are being added to your account.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Purchase Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Package:</span>
              <span className="text-sm">{purchaseDetails.packageName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Credits:</span>
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                +{purchaseDetails.credits.toLocaleString()}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Amount Paid:</span>
              <span className="text-sm font-medium">${purchaseDetails.amount}</span>
            </div>
          </div>

          {/* Instructions */}
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Your credits are being processed and should appear in your account shortly.</p>
            <p>Click the button below to refresh your credit balance:</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              onClick={handleRefreshCredits}
              disabled={refreshing}
              className="flex-1"
            >
              {refreshing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Refresh Credits
                </>
              )}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}